import { css } from "@emotion/css"
import React, { useId } from "react"

import MyPageErrorMessages from "./MyPageErrorMessages"
import FieldDescription from "./form/FieldDescription"
import FieldLabel from "./form/FieldLabel"
import Select from "./form/Select"
import TextInput from "./form/input/TextInput"
import Password from "./form/password/Password"

const targets = new Set<string | React.JSXElementConstructor<any>>([TextInput, Password, Select])

type Props = {
  label: string
  fieldWidth?: number
  children?: React.ReactNode
  render?: (props: { id?: string }) => React.ReactNode
  errorMessages?: readonly string[]
  fieldDescription?: string
  passId?: boolean
  asGroup?: boolean
  type?: "required" | "optional"
  labelAlign?: "left" | "right"
  alignBaseLine?: boolean
}

const MyPageFormField = ({
  label,
  fieldWidth,
  children,
  render,
  errorMessages,
  fieldDescription,
  passId = false,
  asGroup = false,
  type,
  labelAlign,
  alignBaseLine = true,
}: Props) => {
  const id = useId()

  const containerProps = asGroup ? { role: "group", "aria-labelledby": id } : {}
  const labelProps = { id, type, labelAlign, asGroup, alignBaseLine }

  return (
    <div {...containerProps} className={styles.container}>
      <span className={styles.label}>
        <FieldLabel {...labelProps}>{label}</FieldLabel>
      </span>
      <div style={{ width: fieldWidth }}>
        {render?.({ id })}
        {React.Children.map(children, (child) => {
          return passId && React.isValidElement(child) && targets.has(child.type)
            ? React.cloneElement(child as React.ReactElement<{ id: string }>, { id })
            : child
        })}
        {fieldDescription && <FieldDescription paddingTop={5}>{fieldDescription}</FieldDescription>}
        <MyPageErrorMessages paddingTop={6}>{errorMessages}</MyPageErrorMessages>
      </div>
    </div>
  )
}

export default MyPageFormField

const styles = {
  container: css`
    display: flex;
  `,
  label: css`
    flex: 1;
  `,
}
